import { useNavigate } from 'react-router';
import { z } from 'zod';
import {
  Button,
  Card,
  Form,
  FormControl,
  FormItem,
  FormMessage,
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@predoc/cow';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Footer } from './Footer';
import { AuthBrandLogo } from './AuthBrandLogo';
import { REGEXP_ONLY_DIGITS } from 'input-otp';
import {
  useConfirmPhoneNumberSetup,
  useConfirmSignInMfa,
} from '../hooks/authApi';
import { ResendMFA } from './ResendMFA';
import { AUTH_PATHS } from '../constants';
import { useMfa } from '../MfaContext';
import { useAuth } from '@/routes/AuthContext';
import { invalidateMfaSetupGuard } from '../hooks/useMfaSetupGuard';
import { readPostLoginRedirect } from '../utils/postLoginRedirect';

const mfaSchema = z.object({
  code: z
    .string()
    .length(6, 'Please enter a valid 6-digit code')
    .regex(/^\d+$/, 'Code must contain only numbers'),
});

type MfaFormData = z.infer<typeof mfaSchema>;

export const MfaConfirm = () => {
  const navigate = useNavigate();
  const { mutateAsync, isPending, error } = useConfirmSignInMfa();
  const {
    mutateAsync: confirmPhoneNumberSetup,
    isPending: isConfirmingPhone,
    error: phoneSetupError,
  } = useConfirmPhoneNumberSetup();
  const { mfaVerificationMode, setMfaVerificationMode, setAuthEmail } =
    useMfa();
  const { user } = useAuth();
  const isPhoneSetupMode = mfaVerificationMode === 'phone-setup';

  const form = useForm<MfaFormData>({
    resolver: zodResolver(mfaSchema),
    defaultValues: {
      code: '',
    },
    mode: 'onSubmit',
  });

  const onSubmit = async (data: MfaFormData) => {
    try {
      const pendingRedirect = readPostLoginRedirect();
      if (isPhoneSetupMode) {
        await confirmPhoneNumberSetup({ code: data.code });
        setMfaVerificationMode(null);
        setAuthEmail(null);
        await invalidateMfaSetupGuard(user?.email);
        toast.success('Phone number successfully verified!');
        navigate(pendingRedirect);
        return;
      }

      await mutateAsync({ code: data.code });
      toast.success('Phone number successfully verified!');
      navigate(pendingRedirect);
    } catch {
      toast.error('Incorrect verification code.');
      // Error is handled by the mutation and displayed via error
    }
  };

  const code = form.watch('code');
  const isValidCode = code?.length === 6 && /^\d+$/.test(code);
  const formError = error || phoneSetupError;
  const submitPending = isPending || isConfirmingPhone;

  return (
    <div className="flex min-h-screen flex-col items-center justify-between bg-gray-50">
      <div className="flex w-full flex-1 items-center justify-center">
        <div className="w-full max-w-md">
          <div className="mb-8 flex justify-center">
            <AuthBrandLogo className="h-12" />
          </div>

          <Card className="px-8 py-10">
            <div className="flex flex-col items-center">
              <h1 className="mb-2 text-[30px] font-semibold text-gray-900">
                Verification Code
              </h1>

              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="w-full space-y-6"
                >
                  <Controller
                    control={form.control}
                    name="code"
                    render={({ field, fieldState }) => (
                      <FormItem className="flex flex-col items-center">
                        <FormControl>
                          <InputOTP
                            pattern={REGEXP_ONLY_DIGITS}
                            maxLength={6}
                            {...field}
                            className="flex justify-center gap-2"
                          >
                            <InputOTPGroup>
                              <InputOTPSlot
                                index={0}
                                className={`h-10 w-10 ${
                                  fieldState.error ? 'border-destructive' : ''
                                }`}
                              />
                              <InputOTPSlot
                                index={1}
                                className={`h-10 w-10 ${
                                  fieldState.error ? 'border-destructive' : ''
                                }`}
                              />
                              <InputOTPSlot
                                index={2}
                                className={`h-10 w-10 ${
                                  fieldState.error ? 'border-destructive' : ''
                                }`}
                              />
                              <InputOTPSlot
                                index={3}
                                className={`h-10 w-10 ${
                                  fieldState.error ? 'border-destructive' : ''
                                }`}
                              />
                              <InputOTPSlot
                                index={4}
                                className={`h-10 w-10 ${
                                  fieldState.error ? 'border-destructive' : ''
                                }`}
                              />
                              <InputOTPSlot
                                index={5}
                                className={`h-10 w-10 ${
                                  fieldState.error ? 'border-destructive' : ''
                                }`}
                              />
                            </InputOTPGroup>
                          </InputOTP>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <div className="flex flex-col items-center">
                    {formError ? (
                      <FormMessage className="text-destructive">
                        Enter a valid verification code.
                      </FormMessage>
                    ) : (
                      <p className="text-sm font-normal">
                        Enter your verification code.
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-3">
                    <Button
                      type="submit"
                      size="lg"
                      disabled={submitPending || !isValidCode}
                      className="w-full"
                      loading={submitPending}
                    >
                      Submit
                    </Button>
                    <Button
                      type="button"
                      size="lg"
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        const destination = isPhoneSetupMode
                          ? AUTH_PATHS.MFA_SETUP_PHONE
                          : AUTH_PATHS.LOGIN;
                        navigate(destination);
                      }}
                    >
                      Cancel Verification
                    </Button>
                    {!isPhoneSetupMode && <ResendMFA />}
                  </div>
                </form>
              </Form>
            </div>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};
